/**
 * cashFlowProjection (#1657 R1) — read the Liquiditätsplanung balance engine off the
 * new ``financial_account`` / ``cash_flow`` twin entities (the post-migration substrate).
 *
 * Exact TS mirror of the Python reverse path in
 * ``backend/src/services/finance/liquiditaet_projection.py``
 * (``account_to_config`` / ``cash_flow_to_record`` / ``cash_flows_to_records``). A
 * ``cash_flow`` entity reconstructs the same ``LiquiditaetRecord`` the legacy facts
 * produced for every field the engine + Ampel verdict + istHorizon + category rollup
 * depend on (value_date, signed amount → direction + total_eur, status → record_role,
 * ledger_category, and ``planned_ref`` → transaction_id grouping). Keep in lockstep with
 * the Python module — the projection parity test asserts byte-identical engine output.
 *
 * Display-only fields (custom label, qty, notes) are NOT on the cash_flow entity by
 * design (it is the cash-movement atom, design doc §4.2/§4.5); the rich ledger detail
 * stays on the retained ``liquiditaet_record`` facts (retain-and-dual-read).
 */
import { categoryLabel, isLedgerCategory } from './ledgerCategory';
import type { LiquiditaetConfig, LiquiditaetRecord, RecordRole } from './types';

const CF_PREFIX = 'cf-';

const STATUS_TO_ROLE: Record<string, RecordRole> = {
  planned: 'planned',
  contracted: 'contracted',
  actual: 'payed',
};

const str = (v: unknown, fallback = ''): string =>
  typeof v === 'string' ? v : fallback;
const num = (v: unknown, fallback = 0): number => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : fallback;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
};
const numOrNull = (v: unknown): number | null => (v == null ? null : num(v));

/** Strip the ``cf-`` entity-id prefix to recover the underlying transaction key.
 *  Exported since #3137: ``bankToRecord`` re-keys a settling bank fact on the same
 *  ``planned_ref``, so BOTH sides of the pairing must strip identically — two
 *  copies of this two-line rule are exactly how the join silently breaks. */
export const stripCf = (id: string): string =>
  id.startsWith(CF_PREFIX) ? id.slice(CF_PREFIX.length) : id;

/** ``financial_account`` entity (+ the retained ``wj_start_month``) → engine config. */
export function accountToConfig(
  account: Record<string, unknown> | null | undefined,
  wjStartMonth = 7,
): LiquiditaetConfig | null {
  if (!account) return null;
  return {
    anchor_date: str(account.opening_balance_date),
    anchor_balance_eur: num(account.opening_balance),
    kk_limit_eur: numOrNull(account.kontokorrent_limit),
    wj_start_month: wjStartMonth,
  };
}

/** A ``cash_flow`` entity → a ``LiquiditaetRecord``-shaped row for the engine fold. */
export function cashFlowToRecord(
  cf: Record<string, unknown>,
): LiquiditaetRecord {
  const amount = num(cf.amount);
  const status = str(cf.status) || 'planned';
  const role = STATUS_TO_ROLE[status] ?? 'planned';
  const total = Math.abs(amount);
  const cfId = str(cf.cash_flow_id);
  const plannedRef = cf.planned_ref;
  const txId =
    typeof plannedRef === 'string' && plannedRef
      ? stripCf(plannedRef)
      : stripCf(cfId);
  const category = isLedgerCategory(cf.ledger_category)
    ? cf.ledger_category
    : 'unsorted';
  const valueDate = str(cf.value_date);
  const sourceRef = str(cf.source_ref);
  // #3065 — the collective-credit settlement date. Same key-not-lifted shape as
  // `counterparty_id` (backend #3029): `wibill_settlement_match` stamps
  // `settled_at` on EVERY expectation a 1:N bank credit settles, but only the
  // FIRST gets `planned_ref`, so expectations 2..N stay `contracted`-only with no
  // `payed` sibling. Refuse a non-string rather than coerce it; stay absent
  // (never `undefined`-as-null) when unset, mirroring the Python fold exactly.
  const settledAt = cf.settled_at;
  const hasSettledAt = typeof settledAt === 'string' && !!settledAt;
  // #3143 — the SAME key-not-lifted shape, for the key #3029 Phase 0 (v33.1.0)
  // added to the Python fold and this mirror did not follow. Closes the parity
  // window recorded at `backend/ARCH.md` § `cash_flow.counterparty_id`:
  //   counterparty_id = cf.get("counterparty_id")
  //   has_counterparty = isinstance(counterparty_id, str) and bool(counterparty_id)
  //   **({"counterparty_id": counterparty_id} if has_counterparty else {}),
  // Verbatim read, type-gated, conditional key — no trim, no normalisation.
  const counterpartyId = cf.counterparty_id;
  const hasCounterpartyId = typeof counterpartyId === 'string' && !!counterpartyId;
  // #3187 — PARITY, not feature. The Python fold has lifted the Notiz since #3027
  //   "notes": cf.get("note") or None
  // and this mirror never followed — the third instance of the key-not-lifted shape
  // #3029 (`counterparty_id`), #3065 (`settled_at`) and #3143 already closed in this
  // very function. Nothing visible changes today: this fold's ledger consumer
  // (`contractedRecordsFrom`) filters to `status === 'contracted'`, while Notizen are
  // written on `actual` bank flows — those take the FACT route (`toBank` →
  // `bankToRecord`), which is what #3187 proper fixes. Closed here so the byte-mirror
  // discipline frontend/ARCH.md declares is actually true.
  //
  // Renamed `note` → `notes` exactly as the Python fold does. Type-gated (the Python
  // `or None` is not) — unreachable in practice since the ontology types `note` as a
  // string, and refusing is the safer half. Conditional key: `undefined` is this
  // mirror's `None` and both read identically to every consumer (`notes?.trim()`).
  const note = cf.note;
  const hasNote = typeof note === 'string' && !!note;
  return {
    transaction_id: txId,
    record_role: role,
    qty: 1,
    unit_price_eur: total,
    total_eur: total,
    value_date: valueDate,
    ledger_category: category,
    direction: amount >= 0 ? 'income' : 'expense',
    label: categoryLabel(category),
    period_ym: valueDate.slice(0, 7),
    derived_fact_id: sourceRef || undefined,
    ...(hasSettledAt ? { settled_at: settledAt as string } : {}),
    ...(hasCounterpartyId ? { counterparty_id: counterpartyId as string } : {}),
    ...(hasNote ? { notes: note as string } : {}),
  };
}

export function cashFlowsToRecords(
  cashFlows: Record<string, unknown>[],
): LiquiditaetRecord[] {
  return cashFlows.map(cashFlowToRecord);
}

/**
 * The record set the balance engine feeds on, post-migration. Reads the migrated
 * ``cash_flow`` entities, with one exception that keeps the live write loop intact:
 * a USER-written planned transaction stays the fact write-of-record (it re-projects
 * to a cash_flow only on a later pipeline run), so we drop its projected copy and
 * overlay the fact version — preserving the instant optimistic curve recompute and a
 * fresh create/edit before the server round-trip. Pre-migration (no cash_flows) it
 * returns the fact records unchanged, so the surface is render-identical either way.
 *
 * The overlaid plan's ``transaction_id`` is re-keyed to its ``derived_fact_id`` so it
 * matches the ENTITY scheme its payed sibling reconstructs (``stripCf(planned_ref)`` =
 * the planned's source id = its ``derived_fact_id``). Without this the fact scheme
 * (``transactions.ts`` strips the ``liq-plan-`` prefix) and the entity scheme diverge,
 * so the #1653 "payed supersedes its own plan" drop misses → the curve double-counts a
 * user-edited plan for an already-paid transaction. (Scheme-tolerant since #3171: the
 * drop also matches the strip-normalised id, covering the settling credit that keys
 * the bare entity scheme while the plan keeps its FULL ``liq-plan-…`` key.)
 */
/**
 * The CONTRACTED records the LEDGER feeds on (#3107) — the compute-path bridge
 * that carries a ``cash_flow`` expectation onto the live Buchungen/Kategorien
 * ledger, which until now read the three legacy fact types exclusively.
 *
 * Why this exists: a wibill tenant's expectations live ONLY as
 * ``cash_flow.status = 'contracted'`` entity attributes — nothing writes
 * ``liquiditaet_record`` for it at all — so ``tx.contracted`` was never
 * populated and BOTH #3065 (``settled_at`` suppressing the "did not happen"
 * grey) and #3026 (judging a payment against its CONTRACT when there is no
 * plan) were inert on the real browser ledger. Direction of travel is
 * entity → ledger, matching #2076's ``liquiditaet_bank_ledger`` and the #2004
 * D3 decision that the entity substrate is the sole source of truth; the
 * ``liquiditaet_*`` fact family stays frozen and is never extended.
 *
 * **Dedupe is on PROVENANCE, never on ``transaction_id``.** The two record sets
 * use different id namespaces and deliberately do not meet: the entity path
 * keys on ``stripCf(planned_ref ?? cash_flow_id)`` while the fact path keys on
 * ``tx-bank-…`` / the native ``transaction_id``. On a ``both``-substrate tenant
 * a migrated contracted flow is a projection of that tenant's OWN fact
 * (``cash_flow_id = 'cf-' + <source derived_fact_id>``), so its reconstructed
 * ``transaction_id`` is ``liq-rec-<uuid>`` — NOT the fact's — and a union keyed
 * on ids would duplicate every contracted record and orphan its ``payed``
 * sibling. ``derived_fact_id`` is identical on both sides by construction (the
 * migration writes ``source_ref = <source derived_fact_id>``), which is why it
 * is the key here, exactly as in ``engineRecordsFrom`` and ``accountRecords``.
 *
 * The EMPTY-id guard (#2611) is mandatory on both sides: a row with no
 * provenance must never match another row with no provenance, or one
 * ``source_ref``-less row would cancel another and drop the set.
 *
 * Two presentation fields are added here that ``cashFlowToRecord`` deliberately
 * does NOT set, because that function is a byte-mirror of the Python
 * ``cash_flow_to_record`` and must stay one:
 *  - ``label`` — ``cashFlowToRecord`` labels every row with its generic category
 *    name, which would render ~4.772 identically-titled "Umsatz Ackerbau" rows
 *    on WF. The raw expectation carries ``counterparty`` (buyer name) and
 *    ``purpose`` ("Rechnung <number>"), so this mirrors ``bankToRecord``'s
 *    established ``counterparty || purpose || <fallback>`` idiom.
 *  - ``account_id`` — ``scopeRecordsToAccount`` routes the per-Konto feed on it;
 *    without it every bridged row would fold onto the overlay-primary account.
 *    Conditional key, mirroring ``plannedToRecord`` (#2211): an account-less
 *    flow stays account-less and lands in the overlay bucket.
 */
export function contractedRecordsFrom(
  cashFlows: Record<string, unknown>[],
  factRecords: LiquiditaetRecord[],
): LiquiditaetRecord[] {
  if (cashFlows.length === 0) return [];
  // Only NON-EMPTY provenance participates — see the empty-id guard above.
  const factProvenance = new Set(
    factRecords.map((r) => r.derived_fact_id).filter((id): id is string => !!id),
  );
  const out: LiquiditaetRecord[] = [];
  for (const cf of cashFlows) {
    if (str(cf.status) !== 'contracted') continue;
    const base = cashFlowToRecord(cf);
    if (base.derived_fact_id && factProvenance.has(base.derived_fact_id)) continue;
    const counterparty = str(cf.counterparty);
    const purpose = str(cf.purpose);
    const accountId = str(cf.account_id);
    const counterpartyId = str(cf.counterparty_id);
    out.push({
      ...base,
      label: counterparty || purpose || base.label,
      ...(accountId ? { account_id: accountId } : {}),
      ...(counterpartyId ? { counterparty_id: counterpartyId } : {}),
    });
  }
  return out;
}

export function engineRecordsFrom(
  cashFlows: Record<string, unknown>[],
  factRecords: LiquiditaetRecord[],
): LiquiditaetRecord[] {
  if (cashFlows.length === 0) return factRecords;
  const userPlanned = factRecords
    .filter((r) => r.record_role === 'planned' && r.source === 'user')
    .map((r) =>
      r.derived_fact_id ? { ...r, transaction_id: r.derived_fact_id } : r,
    );
  const userPlannedIds = new Set(userPlanned.map((r) => r.transaction_id));
  // Guard the EMPTY id explicitly (#2611): a record with no source ref must
  // never match a user plan that also has none. Since the balance history is
  // now fed by aggregated per-day buckets — which carry no ``source_ref`` at
  // all — an empty-vs-empty match would drop the ENTIRE history and collapse
  // the curve to the anchor, rather than dropping the handful of rows it could
  // have cost before.
  const entityRecords = cashFlowsToRecords(cashFlows).filter(
    (r) => !r.derived_fact_id || !userPlannedIds.has(r.derived_fact_id),
  );
  return [...entityRecords, ...userPlanned];
}
